import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  RestorePageOutlined as RestoreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ProfileValidationService } from '../../services/profileValidationService';
import {
  ProfileCompletenessConfig as ConfigType,
  FieldCompletenessConfig,
} from '../../types/admin';
import { useUser } from '../../contexts/AuthContext';

/**
 * ProfileCompletenessConfig Component
 * Admin page for configuring profile completeness calculation
 *
 * Features:
 * - Enable/disable fields
 * - Set field weights
 * - Preview weight distribution
 * - Restore defaults
 */
const ProfileCompletenessConfig: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useUser();

  // State
  const [config, setConfig] = useState<ConfigType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  /**
   * Load configuration on mount
   */
  useEffect(() => {
    loadConfig();
  }, []);

  /**
   * Load current configuration
   */
  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ProfileValidationService.getConfig();
      setConfig(data);
    } catch (err: any) {
      console.error('Error loading config:', err);
      setError(err.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Save configuration
   */
  const handleSave = async () => {
    if (!config || !currentUser) return;

    // Validate configuration
    const validation = ProfileValidationService.validateConfig(config);
    if (!validation.valid) {
      setValidationErrors(validation.errors);
      return;
    }

    try {
      setSaving(true);
      setValidationErrors([]);
      await ProfileValidationService.saveConfig(
        config,
        currentUser.id,
        currentUser.displayName || currentUser.email || 'Admin'
      );
      setSuccessMessage('Configuration saved successfully!');

      // Reload to ensure we have the latest
      await loadConfig();
    } catch (err: any) {
      console.error('Error saving config:', err);
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Toggle field enabled state
   */
  const handleToggleField = (fieldIndex: number) => {
    if (!config) return;
    const updated = { ...config };
    updated.fields[fieldIndex].enabled = !updated.fields[fieldIndex].enabled;
    setConfig(updated);
  };

  /**
   * Update field weight
   */
  const handleWeightChange = (fieldIndex: number, value: string) => {
    if (!config) return;
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 0 || numValue > 100) return;

    const updated = { ...config };
    updated.fields[fieldIndex].weight = numValue;
    setConfig(updated);
  };

  /**
   * Calculate total weight
   */
  const getTotalWeight = (): number => {
    if (!config) return 0;
    return config.fields
      .filter(f => f.enabled)
      .reduce((sum, f) => sum + f.weight, 0);
  };

  /**
   * Get weight distribution by category
   */
  const getWeightByCategory = (): Record<string, number> => {
    if (!config) return {};
    const distribution: Record<string, number> = {};

    config.fields
      .filter(f => f.enabled)
      .forEach(f => {
        distribution[f.category] = (distribution[f.category] || 0) + f.weight;
      });

    return distribution;
  };

  /**
   * Get enabled field count
   */
  const getEnabledCount = (): number => {
    if (!config) return 0;
    return config.fields.filter(f => f.enabled).length;
  };

  /**
   * Group fields by category
   */
  const getFieldsByCategory = () => {
    if (!config) return {};
    return config.fields.reduce((acc, field) => {
      if (!acc[field.category]) {
        acc[field.category] = [];
      }
      acc[field.category].push(field);
      return acc;
    }, {} as Record<string, FieldCompletenessConfig[]>);
  };

  /**
   * Restore default configuration
   */
  const handleRestoreDefaults = async () => {
    if (!currentUser) return;
    if (!window.confirm('Are you sure you want to restore default configuration? This will reset all your custom weights.')) {
      return;
    }

    try {
      setSaving(true);
      // Clear cache to force fresh default config
      ProfileValidationService.clearCache();

      // Delete current config by saving a fresh default
      const defaultConfig = await ProfileValidationService.getConfig();
      setConfig(defaultConfig);
      setSuccessMessage('Restored to default configuration');
    } catch (err: any) {
      console.error('Error restoring defaults:', err);
      setError(err.message || 'Failed to restore defaults');
    } finally {
      setSaving(false);
    }
  };

  // Category order and display names
  const categoryOrder = ['basic', 'dates', 'locations', 'biographical', 'additional'];
  const categoryLabels: Record<string, string> = {
    basic: 'Basic Information',
    dates: 'Dates',
    locations: 'Locations',
    biographical: 'Biographical',
    additional: 'Additional Information',
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!config) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Failed to load configuration</Alert>
      </Container>
    );
  }

  const fieldsByCategory = getFieldsByCategory();
  const weightByCategory = getWeightByCategory();
  const totalWeight = getTotalWeight();
  const enabledCount = getEnabledCount();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/admin')} aria-label="back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Profile Completeness Configuration
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            startIcon={<RestoreIcon />}
            onClick={handleRestoreDefaults}
            disabled={saving}
          >
            Restore Defaults
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadConfig}
            disabled={saving}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </Box>
      </Box>

      {/* Error Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setValidationErrors([])}>
          <Typography variant="subtitle2" gutterBottom>Validation Errors:</Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationErrors.map((err, idx) => (
              <li key={idx}>{err}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Enabled Fields
              </Typography>
              <Typography variant="h3">
                {enabledCount} / {config.fields.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Weight
              </Typography>
              <Typography variant="h3">{totalWeight}</Typography>
              <Typography variant="caption" color="textSecondary">
                Sum of all enabled field weights
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Last Updated
              </Typography>
              <Typography variant="body1">
                {config.updatedByName}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {new Date(config.updatedAt).toLocaleDateString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Weight Distribution by Category */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Weight Distribution by Category
        </Typography>
        <Grid container spacing={2}>
          {categoryOrder.map(category => (
            <Grid item xs={12} sm={6} md={4} key={category}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  {categoryLabels[category]}
                </Typography>
                <Chip
                  label={`${weightByCategory[category] || 0} pts`}
                  size="small"
                  color={weightByCategory[category] > 0 ? 'primary' : 'default'}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Instructions */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          Configure which fields contribute to profile completeness and their relative importance.
          The completeness percentage is calculated as: (sum of filled field weights) / (sum of all enabled field weights) × 100.
          Higher weights mean the field has more impact on the completeness score.
        </Typography>
      </Alert>

      {/* Field Configuration Tables by Category */}
      {categoryOrder.map(category => {
        const fields = fieldsByCategory[category] || [];
        if (fields.length === 0) return null;

        return (
          <Paper key={category} sx={{ mb: 3 }}>
            <Box sx={{ p: 2, bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                {categoryLabels[category]}
                <Chip
                  label={`${fields.filter(f => f.enabled).length}/${fields.length} enabled`}
                  size="small"
                  sx={{ ml: 2 }}
                />
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="40%">Field</TableCell>
                    <TableCell width="20%" align="center">Enabled</TableCell>
                    <TableCell width="20%" align="center">Weight</TableCell>
                    <TableCell width="20%" align="center">% of Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((field, idx) => {
                    const fieldIndex = config.fields.indexOf(field);
                    const percentage = totalWeight > 0 && field.enabled
                      ? ((field.weight / totalWeight) * 100).toFixed(1)
                      : '0.0';

                    return (
                      <TableRow key={field.field} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={field.enabled ? 500 : 400}>
                            {field.label}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {field.field}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={field.enabled}
                            onChange={() => handleToggleField(fieldIndex)}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            value={field.weight}
                            onChange={(e) => handleWeightChange(fieldIndex, e.target.value)}
                            inputProps={{ min: 0, max: 100, step: 1 }}
                            size="small"
                            sx={{ width: 80 }}
                            disabled={!field.enabled}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            color={field.enabled ? 'primary' : 'textSecondary'}
                          >
                            {percentage}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })}

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProfileCompletenessConfig;
